/**
 * Audit-log masking for secret fields.
 *
 * Audit entries are readable by every role, so they must never carry the
 * plaintext of passwords, PINs, security answers, tax IDs or bank numbers.
 * Secret fields are recorded with a fixed marker instead: the entry still
 * says *that* the field changed (or was cleared), never *what* it holds.
 */

export const SECRET_MASK = '••••';

const SECRET_FIELD_PATTERNS: RegExp[] = [
  /pass\s*(word|code|phrase)?/i, // Password, Passcode, "Pass"
  /\bpwd\b/i,
  /\bpin\b/i,
  /secret/i,
  /token/i,
  /security\s*(answer|question)/i,
  /\banswers?\b/i,
  /\bssn\b/i,
  /social\s*security/i,
  /tax\s*id/i,
  /\b(f?ein|itin|tin)\b/i,
  /routing/i,
  /account\s*(number|no\.?|#)/i,
  /\bcredentials?\b/i,
];

/** True when a column/field name holds secret material. */
export function isSecretField(fieldName: string | null | undefined): boolean {
  if (!fieldName) return false;
  return SECRET_FIELD_PATTERNS.some(re => re.test(fieldName));
}

function isEmpty(value: unknown): boolean {
  return value === null || value === undefined || value === '';
}

/**
 * Mask one value for a field. Empty values stay as they are (so a cleared
 * secret is still visible as cleared); anything else becomes SECRET_MASK.
 */
export function maskAuditValue<T>(
  fieldName: string | null | undefined,
  value: T
): T | string {
  if (!isSecretField(fieldName) || isEmpty(value)) return value;
  return SECRET_MASK;
}

/** Mask every secret key of a row-data object (for row insert/delete logs). */
export function maskSecretFields<T>(value: T): T {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return value;
  }
  const out: Record<string, unknown> = {};
  for (const [key, v] of Object.entries(value as Record<string, unknown>)) {
    out[key] = maskAuditValue(key, v);
  }
  return out as T;
}

/**
 * Mask an audit entry's old/new values: by column name when the entry is
 * about a single field, by key when the values are whole-row objects.
 */
export function maskAuditValues(
  columnName: string | null | undefined,
  oldValue: unknown,
  newValue: unknown
): { old_value: unknown; new_value: unknown } {
  if (columnName && isSecretField(columnName)) {
    return {
      old_value: maskAuditValue(columnName, oldValue),
      new_value: maskAuditValue(columnName, newValue),
    };
  }
  return {
    old_value: maskSecretFields(oldValue),
    new_value: maskSecretFields(newValue),
  };
}

/** Drop masked keys from a row-data object (used when restoring a row). */
export function stripMaskedFields<T extends Record<string, unknown>>(
  data: T
): Partial<T> {
  const out: Partial<T> = {};
  for (const [key, v] of Object.entries(data)) {
    if (v !== SECRET_MASK) (out as Record<string, unknown>)[key] = v;
  }
  return out;
}
